import { styled } from "goober";
import { useMemo, useState } from "react";
import {
  defaultManagedAIProvider,
  defaultManagedChatModel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import {
  isManagedAiConnectorId,
  MANAGED_AI_CONNECTOR_ID,
  MANAGED_AI_CONNECTOR_LABEL,
} from "../../../shared/aiProviders/managedAiConnector";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { useConnectorUsageDaily } from "../../hooks/useConnectorUsageDaily";
import { useManagedAIProvider } from "../../hooks/useManagedAIProvider";
import { useQuery } from "../../hooks/useQuery";
import { ConnectorLogo } from "../ConnectorLogo";
import { connectorProducesTokens } from "../../lib/connectorDirectory";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ProviderLogo } from "../ProviderLogo";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { AiModelsTablePanel } from "./AiModelsTablePanel";
import { AiOverviewStats } from "./AiOverviewStats";
import { AiSpendByModelPanel } from "./AiSpendByModelPanel";
import { AiSpendVsLimitsPanel } from "./AiSpendVsLimitsPanel";
import {
  computeAiProviderMonitoringRows,
  computeModelCostRows,
  type ConnectorAiProvider,
  providerLabel,
  spendCapsForConnectors,
} from "./aiProviderMonitoring";
import {
  buildUsagePriceLookup,
  managedAiDefaultPrices,
} from "../../lib/connectorUsage";
import {
  adaptConnectorUsageToAiRecords,
  computeAiUsageSummary,
  type ConnectorProviderMapping,
} from "./aiUsage";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MONITORING_PANEL_HEIGHT } from "./monitoringPanelLayout";
import { AiGrid, Content } from "./workflowsLayout";

const ALL_PROVIDERS = "__all__";
const AI_TOKEN_MEASURES = [
  "input_tokens",
  "output_tokens",
  "cache_read_tokens",
  "cache_write_tokens",
];

// Empty state fills one large card (like the Value tab); the populated view
// breaks into the smaller panels below.
const EmptyCard = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 32px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProviderTrigger = styled(ControlButton)`
  max-width: 280px;
`;

function LoadingPlaceholder() {
  return (
    <Content aria-hidden>
      <AiGrid>
        <Skeleton style={{ width: "100%", height: 90, borderRadius: 12 }} />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: MONITORING_PANEL_HEIGHT,
            borderRadius: 12,
          }}
        />
      </AiGrid>
    </Content>
  );
}

export function AiPanel() {
  const since = roundedHoursAgo(30 * 24);
  const [providers, providersQuery] = useQuery(queries.monitorAiProviders());
  const [connectors, connectorsQuery] = useQuery(queries.monitorAiConnectors());
  const [pricingRows, pricingQuery] = useQuery(queries.monitoringUsagePrices());
  const [budgets, budgetsQuery] = useQuery(queries.monitoringUsageBudgets());
  const { managedProvider } = useManagedAIProvider();
  const managed = useMemo(
    () =>
      managedProvider
        ? {
            label: MANAGED_AI_CONNECTOR_LABEL,
            chatModel: defaultManagedChatModel,
            fastModel: defaultManagedAIProvider.fastModel,
          }
        : null,
    [managedProvider]
  );
  // Managed AI has no price rows, so seed its sentinel from the list-price
  // estimates; other known models fall back per-measure in estimateTokenCostUsd.
  const prices = useMemo(
    () => buildUsagePriceLookup(pricingRows ?? [], managedAiDefaultPrices()),
    [pricingRows]
  );

  const connectorById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof connectors>[number]>();
    for (const connector of connectors ?? []) map.set(connector.id, connector);
    return map;
  }, [connectors]);

  // Each AI provider is metered in the connector ledger under its backing
  // connector; map connector → provider so the ledger's connectorId rows regroup
  // by aiProviderId the way the rest of the tab expects. AI-capable connectors
  // with no configured ai_provider keep their own id as the grouping key so they
  // surface as their own provider rows rather than being dropped.
  const connectorProviders = useMemo(() => {
    const map = new Map<string, ConnectorProviderMapping>();
    for (const provider of providers ?? []) {
      const connectorId = provider.appUsage?.connectedAppId;
      if (connectorId) {
        map.set(connectorId, {
          aiProviderId: provider.id,
          providerType: provider.provider ?? "",
        });
      }
    }
    for (const connector of connectors ?? []) {
      if (map.has(connector.id)) continue;
      if (isManagedAiConnectorId(connector.id)) continue;
      if (!connectorProducesTokens(connector.identifier)) continue;
      map.set(connector.id, {
        aiProviderId: connector.id,
        providerType: connector.identifier,
      });
    }
    return map;
  }, [providers, connectors]);
  const aiConnectorIds = useMemo(() => {
    const ids = new Set<string>([MANAGED_AI_CONNECTOR_ID]);
    for (const provider of providers ?? []) {
      const connectorId = provider.appUsage?.connectedAppId;
      if (connectorId) ids.add(connectorId);
    }
    for (const connector of connectors ?? []) {
      if (
        isManagedAiConnectorId(connector.id) ||
        connectorProducesTokens(connector.identifier)
      ) {
        ids.add(connector.id);
      }
    }
    return [...ids];
  }, [providers, connectors]);
  const [tokenLedgerRecords, tokenUsageQuery, attributionWorkflows] =
    useConnectorUsageDaily({
      since,
      measures: AI_TOKEN_MEASURES,
    });
  const [requestLedgerRecords, requestUsageQuery] = useConnectorUsageDaily(
    {
      since,
      measures: ["requests"],
      connectorIds: aiConnectorIds,
    },
    {
      enabled:
        providersQuery.type === "complete" &&
        connectorsQuery.type === "complete",
    }
  );
  const workflowById = useMemo(
    () =>
      new Map(
        (attributionWorkflows ?? []).map(
          (workflow) => [workflow.id, { space: workflow.space }] as const
        )
      ),
    [attributionWorkflows]
  );
  const ledgerRecords = useMemo(
    () => [...(tokenLedgerRecords ?? []), ...(requestLedgerRecords ?? [])],
    [tokenLedgerRecords, requestLedgerRecords]
  );
  const usageRecords = useMemo(
    () =>
      adaptConnectorUsageToAiRecords(
        ledgerRecords,
        connectorProviders,
        workflowById
      ),
    [ledgerRecords, connectorProviders, workflowById]
  );

  // AI usage that flowed through a connector with no configured ai_provider is
  // tagged with the connector id as its aiProviderId; surface each such
  // connector as its own provider row so its models are visible and selectable.
  const connectorProviderRows = useMemo<ConnectorAiProvider[]>(() => {
    const seen = new Set<string>();
    const result: ConnectorAiProvider[] = [];
    for (const record of usageRecords) {
      const connectorId = record.connectorId;
      if (!connectorId || isManagedAiConnectorId(connectorId)) continue;
      if (record.aiProviderId !== connectorId) continue;
      if (seen.has(connectorId)) continue;
      seen.add(connectorId);
      const connector = connectorById.get(connectorId);
      result.push({
        id: connectorId,
        label: connector?.name ?? "Connector",
        providerType: connector?.identifier ?? record.provider,
      });
    }
    return result;
  }, [usageRecords, connectorById]);

  const rows = useMemo(
    () =>
      computeAiProviderMonitoringRows(
        providers ?? [],
        usageRecords,
        prices,
        managed,
        connectorProviderRows
      ),
    [providers, usageRecords, prices, managed, connectorProviderRows]
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const defaultId = rows.length > 1 ? ALL_PROVIDERS : (rows[0]?.id ?? null);
  const selectionValid =
    selectedId === ALL_PROVIDERS ||
    (selectedId != null && rows.some((r) => r.id === selectedId));
  const effectiveId = selectionValid ? selectedId : defaultId;
  const isAll = effectiveId === ALL_PROVIDERS;

  const providerRecords = useMemo(
    () =>
      isAll
        ? usageRecords
        : usageRecords.filter((r) =>
            effectiveId === MANAGED_AI_PROVIDER_ID
              ? r.aiProviderId == null
              : r.aiProviderId === effectiveId
          ),
    [usageRecords, effectiveId, isAll]
  );
  const summary = useMemo(
    () => computeAiUsageSummary(providerRecords, prices),
    [providerRecords, prices]
  );
  const modelRows = useMemo(
    () =>
      isAll
        ? computeModelCostRows(providerRecords, prices)
        : (rows.find((r) => r.id === effectiveId)?.modelRows ?? []),
    [isAll, providerRecords, prices, rows, effectiveId]
  );
  // AI spend is metered in the connector ledger, so a provider's caps live in
  // connector_usage_budgets keyed by its backing connector: the managed sentinel
  // for managed AI, a configured provider's connector, or a connector row's own
  // id. The all-providers view aggregates only the connectors surfaced as AI
  // providers here, so a budget on an idle or non-AI connector never leaks in.
  const capConnectorIds = useMemo(() => {
    if (!isAll) {
      if (effectiveId === MANAGED_AI_PROVIDER_ID)
        return [MANAGED_AI_CONNECTOR_ID];
      const provider = (providers ?? []).find((p) => p.id === effectiveId);
      const connectorId = provider
        ? provider.appUsage?.connectedAppId
        : effectiveId;
      return connectorId ? [connectorId] : [];
    }
    const ids = new Set<string>();
    for (const provider of providers ?? []) {
      if (provider.appUsage?.connectedAppId)
        ids.add(provider.appUsage.connectedAppId);
    }
    if (managed) ids.add(MANAGED_AI_CONNECTOR_ID);
    for (const connector of connectorProviderRows) ids.add(connector.id);
    return [...ids];
  }, [isAll, effectiveId, providers, managed, connectorProviderRows]);
  const caps = useMemo(
    () => spendCapsForConnectors(budgets ?? [], capConnectorIds),
    [budgets, capConnectorIds]
  );

  if (
    providersQuery.type !== "complete" ||
    connectorsQuery.type !== "complete" ||
    tokenUsageQuery.type !== "complete" ||
    requestUsageQuery.type !== "complete" ||
    pricingQuery.type !== "complete" ||
    budgetsQuery.type !== "complete"
  ) {
    return (
      <MonitoringLayout>
        <LoadingPlaceholder />
      </MonitoringLayout>
    );
  }

  if (rows.length === 0) {
    return (
      <MonitoringLayout>
        <Content>
          <EmptyCard>
            <EmptyState
              iconName="brain"
              title="No AI providers"
              description="Add a provider in Settings → AI to start tracking cost and access."
              maxWidth={420}
            />
          </EmptyCard>
        </Content>
      </MonitoringLayout>
    );
  }

  const selectedRow = isAll
    ? null
    : (rows.find((r) => r.id === effectiveId) ?? null);
  const selectedProvider = isAll
    ? null
    : ((providers ?? []).find((p) => p.id === effectiveId) ?? null);
  const selectedConnector = isAll
    ? null
    : (connectorProviderRows.find((c) => c.id === effectiveId) ?? null);

  const providerPicker = (
    <Dropdown
      align="bottom"
      opener={
        <ProviderTrigger type="button">
          {isAll ? (
            <Icon name="layers" size={16} color="var(--text-primary)" />
          ) : effectiveId === MANAGED_AI_PROVIDER_ID && managedProvider ? (
            <Icon name="brain" size={16} color="var(--text-primary)" />
          ) : selectedProvider ? (
            <ProviderLogo
              provider={resolveAIProviderId(selectedProvider)}
              size={16}
            />
          ) : selectedConnector ? (
            <ConnectorLogo
              template={{
                name: selectedConnector.label,
                identifier: selectedConnector.providerType,
              }}
              size={16}
            />
          ) : null}
          <Text variant="text-small-md" ellipsis>
            {isAll ? "All providers" : (selectedRow?.label ?? "")}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </ProviderTrigger>
      }
      sections={[
        {
          items: [
            ...(rows.length > 1
              ? [
                  {
                    type: "button" as const,
                    key: ALL_PROVIDERS,
                    left: (
                      <Icon
                        name="layers"
                        size={16}
                        color="var(--text-primary)"
                      />
                    ),
                    label: "All providers",
                    selected: isAll,
                    onClick: () => setSelectedId(ALL_PROVIDERS),
                  },
                ]
              : []),
            ...(managedProvider
              ? [
                  {
                    type: "button" as const,
                    key: MANAGED_AI_PROVIDER_ID,
                    left: (
                      <Icon
                        name="brain"
                        size={16}
                        color="var(--text-primary)"
                      />
                    ),
                    label: MANAGED_AI_CONNECTOR_LABEL,
                    selected: effectiveId === MANAGED_AI_PROVIDER_ID,
                    onClick: () => setSelectedId(MANAGED_AI_PROVIDER_ID),
                  },
                ]
              : []),
            ...(providers ?? []).map((provider) => ({
              type: "button" as const,
              key: provider.id,
              left: (
                <ProviderLogo
                  provider={resolveAIProviderId(provider)}
                  size={16}
                />
              ),
              label: providerLabel(provider),
              selected: provider.id === effectiveId,
              right: provider.active ? (
                <Icon name="check-filled" size={12} color="var(--green-600)" />
              ) : undefined,
              onClick: () => setSelectedId(provider.id),
            })),
            ...connectorProviderRows.map((connector) => ({
              type: "button" as const,
              key: connector.id,
              left: (
                <ConnectorLogo
                  template={{
                    name: connector.label,
                    identifier: connector.providerType,
                  }}
                  size={16}
                />
              ),
              label: connector.label,
              selected: connector.id === effectiveId,
              onClick: () => setSelectedId(connector.id),
            })),
          ],
        },
      ]}
    />
  );

  return (
    <MonitoringLayout>
      <Content>
        <AiGrid>
          <AiOverviewStats summary={summary} picker={providerPicker} />
          <AiSpendVsLimitsPanel
            providerId={isAll ? null : effectiveId}
            limitAmount={caps.total}
            spendUsd={summary.estimatedCostUsd}
            spaceBudgets={caps.spaces}
            usageRecords={usageRecords}
            prices={prices}
          />
          <AiSpendByModelPanel modelRows={modelRows} />
          <AiModelsTablePanel records={providerRecords} prices={prices} />
        </AiGrid>
      </Content>
    </MonitoringLayout>
  );
}
